import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import headerIcon from "../assets/header-icon.png";
import { useUser } from "../context/UserContext";
import {
  applyJourneyOrgToAuthorizationParams,
  applyJourneyOrgToAuthorizationParamsWithClientFallback,
  getApplicationClientIdFromIdTokenClaims,
  getConfig,
} from "../config";

const NavBar = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { selectedLocale, setSelectedLocale, selectedJourney, getClientConfig } = useUser();
  const {
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
    getIdTokenClaims,
  } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate(); 

  const isProfilePage = location.pathname === "/profile";

  const displayName =
    user?.name ||
    user?.nickname ||
    user?.given_name ||
    user?.email?.split("@")[0];

  const handleLogin = async () => {
    const config = getClientConfig();

    alert(
      `Selected locale: ${selectedLocale}\nSelected journey: ${selectedJourney}\nUsing clientId: ${config.clientId}`
    );

    await loginWithRedirect({
      authorizationParams: applyJourneyOrgToAuthorizationParams(
        { ui_locales: selectedLocale },
        config
      ),
    });
  };

  const handleProfile = async () => {
    let applicationClientId;
    if (isAuthenticated) {
      try {
        applicationClientId = getApplicationClientIdFromIdTokenClaims(
          await getIdTokenClaims()
        );
      } catch {
        applicationClientId = undefined;
      }
    }
    const appConfig = getConfig();
    await loginWithRedirect({
      authorizationParams: isAuthenticated
        ? applyJourneyOrgToAuthorizationParamsWithClientFallback(
            {
              ui_locales: selectedLocale,
              "ext-manage-profile": true,
            },
            getClientConfig(),
            appConfig.journeyConfig,
            applicationClientId
          )
        : applyJourneyOrgToAuthorizationParams(
            {
              ui_locales: selectedLocale,
              "ext-manage-profile": true,
            },
            getClientConfig()
          ),
    });
  };

  if (isProfilePage) {
    return (
      <nav className="navbar">
        <div className="nav-left">
          <img src={headerIcon} alt="Shell Logo" className="shell-logo" />
        </div>

        <div className="nav-right">
          <button className="back-to-app-btn" onClick={() => navigate("/")}>
            Back to App
          </button>
          {isAuthenticated && (
            <div className="user-profile">
              <div className="user-avatar">
                <span className="avatar-initials">
                  {displayName?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <span className="user-name">{displayName}</span>
            </div>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar">
      <div className="nav-left">
        {!isAuthenticated ? (
          <button className="login-btn" onClick={handleLogin}>
            LOGIN
          </button>
        ) : (
          <div className="user-info" style={{ position: 'relative' }}>
            <div
              className="user-name-wrapper"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-profile">
                <div className="user-avatar">
                  <span className="avatar-initials">
                    {displayName?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <span className="user-name">{displayName}</span>
              </div>
              <span className="dropdown-arrow">▼</span>
            </div>
            {showDropdown && (
              <div className="dropdown-menu">
                <button 
                  className="sign-out-btn" 
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nav-center">
        <img src={headerIcon} alt="Shell Logo" className="shell-logo" />
      </div>

      <div className="nav-right">
        <div className="locale-wrapper">
          <label htmlFor="locale-select">Locale:</label>
          <select
            id="locale-select"
            className="locale-select"
            value={selectedLocale}
            onChange={(e) => setSelectedLocale(e.target.value)}
          >
            <option value="ar-AE">Arabic [ar-AE]</option>
            <option value="ar-EG">Arabic [ar-EG]</option>
            <option value="ar-KW">Arabic [ar-KW]</option>
            <option value="ar-OM">Arabic [ar-OM]</option>
            <option value="ar-SA">Arabic [ar-SA]</option>
            <option value="bg-BG">Bulgarian [bg-BG]</option>
            <option value="cs-CZ">Czech [cs-CZ]</option>
            <option value="da-DK">Danish [da-DK]</option>
            <option value="de-AT">German [de-AT]</option>
            <option value="de-BE">German [de-BE]</option>
            <option value="de-CH">German [de-CH]</option>
            <option value="de-DE">German [de-DE]</option>
            <option value="de-LI">German [de-LI]</option>
            <option value="de-LU">German [de-LU]</option>
            <option value="de-VA">German [de-VA]</option>
            <option value="el-GR">Greek [el-GR]</option>
            <option value="en-AG">English [en-AG]</option>
            <option value="en-AI">English [en-AI]</option>
            <option value="en-AS">English [en-AS]</option>
            <option value="en-AU">English [en-AU]</option>
            <option value="en-BB">English [en-BB]</option>
            <option value="en-BM">English [en-BM]</option>
            <option value="en-BS">English [en-BS]</option>
            <option value="en-CA">English [en-CA]</option>
            <option value="en-DM">English [en-DM]</option>
            <option value="en-EE">English [en-EE]</option>
            <option value="en-EG">English [en-EG]</option>
            <option value="en-GB">English [en-GB]</option>
            <option value="en-GD">English [en-GD]</option>
            <option value="en-GR">English [en-GR]</option>
            <option value="en-GU">English [en-GU]</option>
            <option value="en-HK">English [en-HK]</option>
            <option value="en-IE">English [en-IE]</option>
            <option value="en-IN">English [en-IN]</option>
            <option value="en-JM">English [en-JM]</option>
            <option value="en-KN">English [en-KN]</option>
            <option value="en-KW">English [en-KW]</option>
            <option value="en-KY">English [en-KY]</option>
            <option value="en-LC">English [en-LC]</option>
            <option value="en-LT">English [en-LT]</option>
            <option value="en-LV">English [en-LV]</option>
            <option value="en-MP">English [en-MP]</option>
            <option value="en-MS">English [en-MS]</option>
            <option value="en-MT">English [en-MT]</option>
            <option value="en-MY">English [en-MY]</option>
            <option value="en-NG">English [en-NG]</option>
            <option value="en-OM">English [en-OM]</option>
            <option value="en-PH">English [en-PH]</option>
            <option value="en-PK">English [en-PK]</option>
            <option value="en-PR">English [en-PR]</option>
            <option value="en-SA">English [en-SA]</option>
            <option value="en-SG">English [en-SG]</option>
            <option value="en-TC">English [en-TC]</option>
            <option value="en-TH">English [en-TH]</option>
            <option value="en-TT">English [en-TT]</option>
            <option value="en-US">English [en-US]</option>
            <option value="en-ZA">English [en-ZA]</option>
            <option value="es-AR">Spanish [es-AR]</option>
            <option value="es-DO">Spanish [es-DO]</option>
            <option value="es-ES">Spanish [es-ES]</option>
            <option value="es-GU">Spanish [es-GU]</option>
            <option value="es-PR">Spanish [es-PR]</option>
            <option value="fi-FI">Finnish [fi-FI]</option>
            <option value="fr-BE">French [fr-BE]</option>
            <option value="fr-CA">French [fr-CA]</option>
            <option value="fr-CH">French [fr-CH]</option>
            <option value="fr-FR">French [fr-FR]</option>
            <option value="fr-LU">French [fr-LU]</option>
            <option value="fr-VA">French [fr-VA]</option>
            <option value="gu-IN">Gujarati [gu-IN]</option>
            <option value="hi-IN">Hindi [hi-IN]</option>
            <option value="hr-BA">Croatian [hr-BA]</option>
            <option value="hr-HR">Croatian [hr-HR]</option>
            <option value="hr-ME">Croatian [hr-ME]</option>
            <option value="hu-HU">Hungarian [hu-HU]</option>
            <option value="id-ID">Indonesian [id-ID]</option>
            <option value="it-CH">Italian [it-CH]</option>
            <option value="it-IT">Italian [it-IT]</option>
            <option value="it-SM">Italian [it-SM]</option>
            <option value="it-VA">Italian [it-VA]</option>
            <option value="ja-JP">Japanese [ja-JP]</option>
            <option value="kk-KZ">Kazakh [kk-KZ]</option>
            <option value="kn-IN">Kannada [kn-IN]</option>
            <option value="ko-KR">Korean [ko-KR]</option>
            <option value="mr-IN">Marathi [mr-IN]</option>
            <option value="ms-BN">Malay [ms-BN]</option>
            <option value="ms-MY">Malay [ms-MY]</option>
            <option value="ms-SG">Malay [ms-SG]</option>
            <option value="nb-NO">Norwegian Bokmål [nb-NO]</option>
            <option value="nl-BE">Dutch [nl-BE]</option>
            <option value="nl-NL">Dutch [nl-NL]</option>
            <option value="nn-NO">Norwegian Nynorsk [nn-NO]</option>
            <option value="no-NO">Norwegian [no-NO]</option>
            <option value="pl-PL">Polish [pl-PL]</option>
            <option value="pt-BR">Portuguese [pt-BR]</option>
            <option value="pt-MO">Portuguese [pt-MO]</option>
            <option value="pt-PT">Portuguese [pt-PT]</option>
            <option value="ro-MD">Romanian [ro-MD]</option>
            <option value="ro-RO">Romanian [ro-RO]</option>
            <option value="ru-RU">Russian [ru-RU]</option>
            <option value="sk-SK">Slovak [sk-SK]</option>
            <option value="sl-SI">Slovenian [sl-SI]</option>
            <option value="sq-AL">Albanian [sq-AL]</option>
            <option value="sq-ME">Albanian [sq-ME]</option>
            <option value="sq-XK">Albanian [sq-XK]</option>
            <option value="sr-BA">Serbian [sr-BA]</option>
            <option value="sr-ME">Serbian [sr-ME]</option>
            <option value="sr-RS">Serbian [sr-RS]</option>
            <option value="sr-XK">Serbian [sr-XK]</option>
            <option value="sv-FI">Swedish [sv-FI]</option>
            <option value="sv-SE">Swedish [sv-SE]</option>
            <option value="ta-IN">Tamil [ta-IN]</option>
            <option value="th-TH">Thai [th-TH]</option>
            <option value="tr-TR">Turkish [tr-TR]</option>
            <option value="uk-UA">Ukrainian [uk-UA]</option>
            <option value="ur-IN">Urdu [ur-IN]</option>
            <option value="ur-PK">Urdu [ur-PK]</option>
            <option value="vi-VN">Vietnamese [vi-VN]</option>
            <option value="zh-CN">Chinese (Simplified) [zh-CN]</option>
            <option value="zh-HK">Chinese (Traditional) [zh-HK]</option>
            <option value="zh-MO">Chinese [zh-MO]</option>
            <option value="zh-SG">Chinese [zh-SG]</option>
          </select>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
